// ── 세무사 급여대장 업로드 섹션 ────────────────────────────────────────────────
// PayrollMonthly.jsx에서 사용
// 1. "세무사랑 파일업로드" 버튼 → xls/xlsx 업로드
// 2. 업로드 이력 테이블 (다운로드/삭제)
// 3. 업로드 결과 미리보기 (매칭 현황)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../../api/apiClient';
import PayrollCompareModal from './PayrollCompareModal';
import { stdBlackBtn } from '../adminlayout/designStandard';

// ── 숫자 포맷 ───────────────────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '0';

// ── 날짜 포맷 (KST 표시) ────────────────────────────────────────────────────
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
// 주요 CTA — 디자인 표준(stdBlackBtn)
const btnUpload = { ...stdBlackBtn };
const btnSmall = (color, bg) => ({
  fontSize: 11, padding: '3px 10px', borderRadius: 5,
  border: `1px solid ${color}`, background: bg || 'var(--bg-card)',
  color, cursor: 'pointer', fontWeight: 600,
});
const thStyle = {
  padding: '6px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
  borderBottom: '1px solid var(--border)', textAlign: 'left', whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '6px 8px', fontSize: 12, color: 'var(--text)', borderBottom: '1px solid var(--border)',
};

export default function PayrollUploadSection({ companyCode, year, month, periodId, isConfirmed = false, showToast, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [expanded, setExpanded] = useState(false);
  // 비교 모달: 열려 있을 때 해당 batchId 저장
  const [compareBatchId, setCompareBatchId] = useState(null);

  // ── 배치 이력 로드 ────────────────────────────────────────────────────────
  const loadBatches = useCallback(async () => {
    const res = await apiClient.get(
      `/api/admin/payroll/upload/batches?company_code=${companyCode}&year=${year}&month=${month}`,
    );
    if (res.ok && res.data?.data) setBatches(res.data.data);
  }, [companyCode, year, month]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // ── 파일 업로드 ───────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      showToast('xlsx, xls, csv 파일만 업로드 가능합니다.', 'error');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // 한글 파일명 깨짐 방지: 파일명을 별도 필드로 전송
      fd.append('originalFileName', file.name);
      fd.append('company_code', companyCode);
      fd.append('year', String(year));
      fd.append('month', String(month));
      const r = await apiClient.postForm('/api/admin/payroll/upload', fd);
      if (!r.data?.success) {
        showToast(r.data?.message || '업로드 실패', 'error');
        return;
      }
      const { totalRows, matchedCount, unmatchedCount, unmatchedNames } = r.data;
      let msg = `업로드 완료 — 전체 ${totalRows}명 (매칭 ${matchedCount}명`;
      if (unmatchedCount > 0) msg += `, 미매칭 ${unmatchedCount}명: ${unmatchedNames.join(', ')}`;
      msg += ')';
      showToast(msg, unmatchedCount > 0 ? 'info' : 'success');
      loadBatches();
      if (onUploaded) onUploaded();
    } catch (err) {
      showToast(err?.response?.data?.message || '업로드 중 오류가 발생했습니다.', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ── CSV 다운로드 ──────────────────────────────────────────────────────────
  const handleDownload = async (batchId, fileName) => {
    const res = await apiClient.get(`/api/admin/payroll/upload/batches/${batchId}/download`);
    if (!res.ok) {
      showToast(typeof res.data === 'string' ? res.data : (res.data?.message || '다운로드 실패'), 'error');
      return;
    }
    // UTF-8 BOM 처리 (한글 Excel 호환)
    const clean = (res.data || '').replace(/^\uFEFF/, '');
    const blob = new Blob(['\uFEFF' + clean], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (fileName || `payroll_batch_${batchId}`).replace(/\.[^.]+$/, '') + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── 배치 삭제 ─────────────────────────────────────────────────────────────
  const handleDelete = async (batchId) => {
    if (!window.confirm('이 업로드 이력을 삭제하시겠습니까?')) return;
    const res = await apiClient.del(`/api/admin/payroll/upload/batches/${batchId}`);
    if (res.ok) { showToast('삭제 완료', 'success'); loadBatches(); }
    else showToast('삭제 실패', 'error');
  };

  const visible = expanded ? batches : batches.slice(0, 2);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-sub)' }}>
          세무사 급여대장
        </div>
        <button style={btnUpload} onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? '업로드 중...' : '세무사랑 파일업로드'}
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleUpload} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          세무사에서 받은 급여대장 xls 파일을 업로드하세요
        </span>
      </div>

      {/* ── 업로드 이력 테이블 ──────────────────────────────────────────────── */}
      {batches.length > 0 ? (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['업로드일시', '파일명', '전체', '매칭', '미매칭', '업로더', ''].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((b) => (
                  <tr key={b.id}>
                    <td style={tdStyle}>{fmtDate(b.uploaded_at)}</td>
                    <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.file_name}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{fmt(b.total_rows)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#15803d', fontWeight: 700 }}>
                      {fmt(b.matched_count)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: b.unmatched_count > 0 ? '#dc2626' : 'var(--text-muted)', fontWeight: 700 }}>
                      {fmt(b.unmatched_count)}
                    </td>
                    <td style={tdStyle}>{b.uploaded_by_name || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {/* 비교 버튼: 급여 계산이 완료된 상태(periodId 존재)에서만 표시 */}
                        {periodId && (
                          <button style={{ ...btnSmall('var(--text-sub)'), borderColor: 'var(--border)' }} onClick={() => setCompareBatchId(b.id)}>
                            비교
                          </button>
                        )}
                        <button style={{ ...btnSmall('var(--text-sub)'), borderColor: 'var(--border)' }} onClick={() => handleDownload(b.id, b.file_name)}>
                          CSV
                        </button>
                        <button style={btnSmall('#dc2626', '#fef2f2')} onClick={() => handleDelete(b.id)}>
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* 펼치기/접기 */}
          {batches.length > 2 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              {expanded ? `▲ 접기` : `▼ 이전 이력 ${batches.length - 2}건 더 보기`}
            </button>
          )}
        </>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
          {year}년 {month}월 업로드 이력이 없습니다.
        </div>
      )}
      {/* ── 비교 모달 ────────────────────────────────────────────────────── */}
      {compareBatchId && periodId && (
        <PayrollCompareModal
          batchId={compareBatchId}
          periodId={periodId}
          isConfirmed={isConfirmed}
          showToast={showToast}
          onClose={() => setCompareBatchId(null)}
          onApplied={() => { loadBatches(); if (onUploaded) onUploaded(); }}
        />
      )}
    </div>
  );
}
